import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsByAgentIdOptions, getOrgAgentsByAgentIdQueryKey, patchOrgAgentsByAgentIdMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { Model } from '@onefootprint/request-types/dashboard';
import { Card, Dialog, Divider, Select, Toggle } from '@onefootprint/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useBuilder } from '../../store/provider';
type ModelConfigDialogProps = {
  agentId: string;
  open: boolean;
  onClose: () => void;
};

// Internal-only employee action, so copy is hardcoded like the others in this menu. Keyed by
// `Model` so a model the backend adds fails typecheck here instead of silently missing an option.
const MODEL_LABELS: Record<Model, string> = {
  claude_opus55: 'Claude Opus 5.5',
  claude_opus48: 'Claude Opus 4.8',
  claude_opus5: 'Claude Opus 5',
  claude_sonnet5: 'Claude Sonnet 5',
  claude_sonnet46: 'Claude Sonnet 4.6',
  claude_haiku45: 'Claude Haiku 4.5',
  glm52: 'GLM 5.2',
  glm53: 'GLM 5.3'
};
const MODEL_OPTIONS = Object.entries(MODEL_LABELS).map(([value, label]) => ({
  value,
  label
}));

// What the agent falls back to when no model is pinned (matches the backend default).
const DEFAULT_MODEL: Model = 'claude_opus55';
const ModelConfigDialog = t0 => {
  const $ = _c(52);
  const {
    agentId,
    open,
    onClose
  } = t0;
  const {
    state,
    mutateOp
  } = useBuilder();
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const savedModel = state.bundle.modelConfig?.model ?? DEFAULT_MODEL;
  const [model, setModel] = useState(savedModel);
  let t1;
  if ($[0] !== agentId) {
    t1 = getOrgAgentsByAgentIdOptions({
      path: {
        agentId
      }
    });
    $[0] = agentId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = open && Boolean(agentId);
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const agentQuery = useQuery(t3);
  const savedMemoryEnabled = agentQuery.data?.memoryEnabled ?? false;
  const [memoryEnabled, setMemoryEnabled] = useState(savedMemoryEnabled);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = patchOrgAgentsByAgentIdMutation();
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== showErrorToast) {
    t5 = {
      ...t4,
      onError: showErrorToast
    };
    $[6] = showErrorToast;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const memoryMutation = useMutation(t5);
  let t6;
  if ($[8] !== open || $[9] !== state.bundle.modelConfig?.model) {
    t6 = () => {
      if (open) {
        setModel(state.bundle.modelConfig?.model ?? DEFAULT_MODEL);
      }
    };
    $[8] = open;
    $[9] = state.bundle.modelConfig?.model;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = state.bundle.modelConfig?.model;
  let t8;
  if ($[11] !== open || $[12] !== t7) {
    t8 = [open, t7];
    $[11] = open;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useEffect(t6, t8);
  let t10;
  let t9;
  if ($[14] !== open || $[15] !== savedMemoryEnabled) {
    t9 = () => {
      if (open) {
        setMemoryEnabled(savedMemoryEnabled);
      }
    };
    t10 = [open, savedMemoryEnabled];
    $[14] = open;
    $[15] = savedMemoryEnabled;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  const modelChanged = model !== savedModel;
  const memoryChanged = memoryEnabled !== savedMemoryEnabled;
  let t11;
  if ($[18] !== agentId || $[19] !== memoryChanged || $[20] !== memoryEnabled || $[21] !== memoryMutation || $[22] !== model || $[23] !== modelChanged || $[24] !== mutateOp || $[25] !== onClose || $[26] !== queryClient) {
    t11 = async () => {
      if (memoryChanged) {
        try {
          await memoryMutation.mutateAsync({
            path: {
              agentId
            },
            body: {
              memoryEnabled
            }
          });
        } catch {
          return;
        }
        await queryClient.invalidateQueries({
          queryKey: getOrgAgentsByAgentIdQueryKey({
            path: {
              agentId
            }
          })
        });
      }
      if (modelChanged) {
        mutateOp({
          kind: "update_model_config",
          modelConfig: {
            model
          }
        });
      }
      onClose();
    };
    $[18] = agentId;
    $[19] = memoryChanged;
    $[20] = memoryEnabled;
    $[21] = memoryMutation;
    $[22] = model;
    $[23] = modelChanged;
    $[24] = mutateOp;
    $[25] = onClose;
    $[26] = queryClient;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleSave = t11;
  const t12 = !modelChanged && !memoryChanged;
  let t13;
  if ($[28] !== handleSave || $[29] !== memoryMutation.isPending || $[30] !== t12) {
    t13 = {
      label: "Save",
      onClick: handleSave,
      disabled: t12,
      loading: memoryMutation.isPending
    };
    $[28] = handleSave;
    $[29] = memoryMutation.isPending;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== memoryMutation.isPending || $[33] !== onClose) {
    t14 = {
      label: "Cancel",
      onClick: onClose,
      disabled: memoryMutation.isPending
    };
    $[32] = memoryMutation.isPending;
    $[33] = onClose;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex flex-col gap-1"><p className="text-label-2 text-primary">Model</p><p className="text-body-2 text-tertiary">Which model this agent runs on. Internal-only — changing a real tenant's model requires risk ops.</p></div>;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = value => setModel(value as Model);
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== model) {
    t17 = <Select size="compact" id="agent-model-select" aria-label="Model" value={model} options={MODEL_OPTIONS} onChange={t16} />;
    $[37] = model;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Divider />;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== agentQuery.isPending || $[41] !== memoryEnabled) {
    t19 = <Toggle fullWidth={true} labelPlacement="left" label="Memory" hint="Every completed run launches a trust fabric trace consumer that records what it learned on the tenant's message board." checked={memoryEnabled} disabled={agentQuery.isPending} onChange={setMemoryEnabled} />;
    $[40] = agentQuery.isPending;
    $[41] = memoryEnabled;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t17 || $[44] !== t19) {
    t20 = <Card.Root><Card.Body><div className="flex flex-col gap-3 p-4">{t15}{t17}{t18}{t19}</div></Card.Body></Card.Root>;
    $[43] = t17;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== onClose || $[47] !== open || $[48] !== t13 || $[49] !== t14 || $[50] !== t20) {
    t21 = <Dialog open={open} onClose={onClose} title="Model configuration" primaryButton={t13} secondaryButton={t14}>{t20}</Dialog>;
    $[46] = onClose;
    $[47] = open;
    $[48] = t13;
    $[49] = t14;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};
export default ModelConfigDialog;